import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { FormattedNumber } from "react-intl";
import moment from "moment";
import ICatalog_Item from "@models/ICatalog_Item";
import { useAppContextNonNull } from "@contexts/AppContext";
import { IReservedOrderDetail } from "@models/DTO/IOnGetReservedOrdersDTO";
import InventoryService from "@services/InventoryService";
import UIService from "@services/UIService";
import OwButton from "@components/shared/OwButton/OwButton";
import { OwIconButton } from "@components/shared/OwIconButton/OwIconButton";
import PaginationBar from "@components/shared/PaginationBar/PaginationBar";
import "./InventoryItemReservedQtyOnHandDetailSidebar.scss";

type Props = {
    catalogItem: ICatalog_Item;
    closeFn: () => void;
};

const DEFAULT_LIMIT = 25;

export const InventoryItemReservedQtyOnHandDetailSidebar = (props: Props) => {
    const { catalogItem, closeFn } = props;
    const { userModel, supplierModel } = useAppContextNonNull();

    const [orders, setOrders] = useState<IReservedOrderDetail[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [totalReservedQty, setTotalReservedQty] = useState(0);
    const [catalogItemSku, setCatalogItemSku] = useState<string | undefined>();
    const [catalogItemName, setCatalogItemName] = useState<string | undefined>();
    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);

    const fetchReservedOrders = useCallback(async () => {
        setIsLoading(true);

        try {
            const result = await InventoryService.getReservedOrders(
                {
                    catalogItemGuid: catalogItem.guid,
                    limit,
                    offset,
                },
                userModel,
                supplierModel
            );

            if (result.success && result.data) {
                setOrders(result.data.orders);
                setTotalCount(result.data.totalCount);
                setTotalReservedQty(result.data.totalReservedQty);
                setCatalogItemSku(result.data.catalogItemSku);
                setCatalogItemName(result.data.catalogItemName);
            }
        } catch (error) {
            console.error("Error fetching reserved orders:", error);
        } finally {
            setIsLoading(false);
        }
    }, [catalogItem.guid, limit, offset, userModel, supplierModel]);

    const handleExportCsv = async () => {
        setIsExporting(true);

        try {
            const result = await InventoryService.getReservedOrdersCsv(catalogItem.guid, userModel, supplierModel);

            if (result.success && result.csvData && result.filename) {
                UIService.downloadCSVData(result.csvData, result.filename);
            }
        } catch (error) {
            console.error("Error exporting reserved orders:", error);
        } finally {
            setIsExporting(false);
        }
    };

    useEffect(() => {
        fetchReservedOrders();
    }, [fetchReservedOrders]);

    const handlePaginationChanged = (newOffset: number, newLimit: number) => {
        setOffset(newOffset);
        setLimit(newLimit);
    };

    const formatDate = (dateString: string) => {
        return moment(dateString).format("MM/DD/YYYY");
    };

    return (
        <div className="InventoryItemReservedQtyOnHandDetailSidebar">
            <div className="sidebar-header">
                <div className="header-content">
                    <h5 className="sidebar-title">Reserved Qty On Hand Details</h5>
                    <div className="item-info">
                        <strong>{catalogItemSku || catalogItem.sku}</strong>
                        {(catalogItemName || catalogItem.description) && <span> - {catalogItemName || catalogItem.description}</span>}
                    </div>
                    <div className="summary-badge">
                        Total Reserved: <FormattedNumber value={totalReservedQty} minimumFractionDigits={0} />
                    </div>
                </div>
                <OwButton centerIconClass="fas fa-times" className="btn-danger" fn={closeFn} />
            </div>

            <div className="actions-container">
                <OwButton
                    fn={handleExportCsv}
                    disabled={isExporting || isLoading || totalCount === 0}
                    title={isExporting ? "Exporting..." : "Export CSV"}
                    leftIconClass="fas fa-file-csv"
                    className="btn-primary btn-sm"
                    skipTranslate
                />
            </div>

            <div className="table-container">
                {isLoading ? (
                    <div className="loading-container">
                        <i className="fas fa-spinner fa-spin fa-2x"></i>
                    </div>
                ) : orders.length === 0 ? (
                    <div className="empty-state">
                        <i className="fas fa-inbox fa-3x mb-3"></i>
                        <p>No reserved orders found for this item.</p>
                    </div>
                ) : (
                    <table className="orders-table">
                        <thead>
                            <tr>
                                <th>Order #</th>
                                <th>Date</th>
                                <th>Customer</th>
                                <th className="text-end">Qty Reserved</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((order) => (
                                <tr key={order.orderGuid}>
                                    <td>
                                        <Link to={`/orders/${order.orderGuid}/entry`} className="text-primary" onClick={closeFn}>
                                            {order.orderNo}
                                        </Link>
                                    </td>
                                    <td>{formatDate(order.orderDate)}</td>
                                    <td>{order.customerName}</td>
                                    <td className="text-end">
                                        <FormattedNumber value={order.qtyReserved} minimumFractionDigits={0} />
                                    </td>
                                    <td>{order.orderState}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {totalCount > 0 && (
                <div className="pagination-container">
                    <PaginationBar
                        totalRecords={totalCount}
                        limit={limit}
                        offset={offset}
                        onPaginationChanged={handlePaginationChanged}
                        isActive={isLoading}
                        hideLimit
                        centered
                    />
                </div>
            )}
        </div>
    );
};
